import React, { useState, useMemo, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { useReportTemplateActions } from "@features/report/template/hooks/useReportTemplateActions";
import { FiFileText, FiPlus } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import { CreateTemplateRequest } from "@features/report/template/types/reportTemplateRequests";
import { OriginScope } from "@features/report/template/types/reportTemplate";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { useErrorModalStore } from '@infrastructure/api/store/errorModalStore';
import { ErrorResponse } from '@core/types/errorResponse';
import InfoTooltip from "@core/components/InfoTooltip";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { usePermissions } from "@features/entity/hooks/usePermissions";
import { ACTION_CODES, TEMPLATE_ORIGIN_CREATE_CODES } from "@features/entity/types/actionCodes";

type FieldErrors = Partial<Record<keyof CreateTemplateRequest, string[]>>;

const CreateTemplateModal: React.FC<{
    show: boolean;
    onHide: () => void;
    departmentId: number;
    nationId: number;
    onChanged?: () => void;
}> = ({ show, onHide, departmentId, nationId, onChanged }) => {
    const [searchParams] = useSearchParams();
    const scopeId = useScopeId();
    const resolvedDepartmentId = departmentId ?? Number(searchParams.get("did"));
    const resolvedNationId = nationId ?? scopeId ?? 0;

    const { hasPermission } = usePermissions({
        scopeType: "NATION",
        scopeId: resolvedNationId || undefined,
        actionCodes: [...TEMPLATE_ORIGIN_CREATE_CODES],
    });

    const canCreateChurch = hasPermission(ACTION_CODES.CREATE_CHURCH_REPORT_TEMPLATE);
    const canCreateDistrict = hasPermission(ACTION_CODES.CREATE_DISTRICT_REPORT_TEMPLATE);
    const canCreateNation = hasPermission(ACTION_CODES.CREATE_NATION_REPORT_TEMPLATE);

    const availableOriginScopes = useMemo(() => {
        const scopes: { value: OriginScope; label: string }[] = [];
        if (canCreateChurch) scopes.push({ value: 'CHURCH', label: 'Church' });
        if (canCreateDistrict) scopes.push({ value: 'DISTRICT', label: 'District' });
        if (canCreateNation) scopes.push({ value: 'NATION', label: 'Nation' });
        return scopes;
    }, [canCreateChurch, canCreateDistrict, canCreateNation]);

    const [form, setForm] = useState<CreateTemplateRequest>({
        report_template_name: "",
        description: "",
        origin_scope: undefined,
    });
    const [formErrors, setFormErrors] = useState<FieldErrors>({});
    const showError = useApiError();
    const showValidation = useErrorModalStore((s) => s.showValidation);
    const { busy, doTemplateCreate } = useReportTemplateActions();

    useEffect(() => {
        if (availableOriginScopes.length === 1 && !form.origin_scope) {
            setForm((f) => ({ ...f, origin_scope: availableOriginScopes[0].value }));
        }
    }, [availableOriginScopes, form.origin_scope]);

    const handleChange = (field: keyof CreateTemplateRequest) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        let value: string | number = e.target.value;

        setForm(f => ({ ...f, [field]: value || undefined }));
        if (formErrors[field]) {
            setFormErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const resetForm = () => {
        setForm({
            report_template_name: "",
            description: "",
            origin_scope: availableOriginScopes.length === 1 ? availableOriginScopes[0].value : undefined,
        });

        setFormErrors({});
    };

    const handleSubmit = async () => {
        const errors: FieldErrors = {};
        const messages: string[] = [];
        if (!form.report_template_name?.trim()) {
            errors.report_template_name = ["Name is required."];
            messages.push("Name is required.");
        }
        if (availableOriginScopes.length > 0 && !form.origin_scope) {
            (errors as any).origin_scope = ["Please select who will complete this template."];
            messages.push("Please select who will complete this template.");
        }
        if (messages.length > 0) {
            setFormErrors(errors);
            showValidation(messages);
            return;
        }

        try {
            const response = await doTemplateCreate(resolvedNationId, resolvedDepartmentId, form);

            if (response.isSuccess()) {
                showSuccess(response.getData()?.message);
                onHide();
                resetForm();

                onChanged?.();
            } else {
                // Handle errors: highlight fields and show validation dialog with backend messages
                const error = response.getError() as ErrorResponse;
                const fieldErrors = (error?.errors as FieldErrors) ?? {};
                setFormErrors(fieldErrors);
                const list = Object.values(fieldErrors).flat().filter(Boolean);
                showValidation(list.length ? list : [error?.detail || 'Failed to create template']);
                showError(error);
            }

        } catch (error) {
            showError(error as ErrorResponse);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
            data-testid="create-template-modal"
        >
            <Modal.Header closeButton className="border-0 pb-0" data-testid="create-template-modal-header">
                <Modal.Title className="fw-semibold" data-testid="create-template-modal-title">
                    <FiFileText className="me-2" aria-hidden />
                    Create New Report Template
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3" data-testid="create-template-modal-body">
                <Form data-testid="create-template-form">
                    <Form.Group className="mb-3" controlId="templateName">
                        <Form.Label>Name<span className="text-danger">*</span><InfoTooltip text="Name of this report template as it appears to users when they choose a template to fill." /></Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Template name"
                            value={form.report_template_name}
                            onChange={handleChange("report_template_name")}
                            isInvalid={!!formErrors.report_template_name}
                        />
                        <Form.Control.Feedback type="invalid" role="alert" data-testid="template-name-error">
                            {formErrors.report_template_name?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="templateDescription">
                        <Form.Label>Description<InfoTooltip text="Optional description of what this template is used for." /></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Template description"
                            value={form.description ?? ""}
                            onChange={handleChange("description")}
                            isInvalid={!!formErrors.description}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formErrors.description?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>
                    {availableOriginScopes.length > 0 && (
                        <Form.Group className="mb-3" controlId="templateOriginScope">
                            <Form.Label>
                                To be completed by <span className="text-danger">*</span>
                                <InfoTooltip text="Select who will be responsible for filling out this report template." />
                            </Form.Label>
                            {availableOriginScopes.length === 1 ? (
                                <Form.Control
                                    type="text"
                                    value={availableOriginScopes[0].label}
                                    disabled
                                    className="bg-light"
                                />
                            ) : (
                                <Form.Select
                                    value={form.origin_scope ?? ""}
                                    onChange={handleChange("origin_scope") as any}
                                    isInvalid={!!(formErrors as any).origin_scope}
                                >
                                    <option value="">Select who will complete this…</option>
                                    {availableOriginScopes.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                            <Form.Control.Feedback type="invalid">
                                {(formErrors as any).origin_scope?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    )}
                    <div className="d-flex justify-content-end mt-3">
                        <Button variant="primary" size="lg" className="btn-action" onClick={handleSubmit} disabled={busy} aria-busy={busy}>
                            {busy ? <>
                                <Spinner size="sm" animation="border" className="me-2" />Creating Template…
                            </> : (
                                <>
                                    <FiPlus className="me-2" />
                                    Create Template
                                </>
                            )}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default CreateTemplateModal;